import { decodePassword, encodePassword } from "@dl/web-core-js/api";
import bcrypt from "bcryptjs";

/**
 * BCrypt, in the browser — in the form `DisplayCollectionDTO` carries it.
 *
 * Two encodings are stacked here, and conflating them is what breaks this
 * screen. The password column stores a BCrypt hash; the DTO then puts that
 * hash on the wire **Base64URL-encoded**, because the Java field is annotated
 * with `StringUrlEncodeSerializer` / `StringUrlDecodeDeserializer`. Sending a
 * bare hash is a hard error rather than a silent one — a BCrypt hash opens
 * `$2a$`, and `$` is not a Base64 character, so the server rejects the whole
 * body with `400 … Illegal base64 character 24`.
 *
 * Both functions below therefore speak the *wire* form, which is what every
 * caller actually holds: what the API returned, or what it is about to send.
 * Neither ever exposes a bare hash, so there is nothing to get the wrong way
 * round at a call site.
 *
 * Cost 10 matches Spring's `BCryptPasswordEncoder` default, so hashes written
 * by either app validate in the other.
 */
export async function hashPassword(plain: string): Promise<string> {
  return encodePassword(await bcrypt.hash(plain, 10));
}

/** `wireHash` is the DTO's field verbatim — Base64URL, not a bare hash. */
export async function verifyPassword(plain: string, wireHash: string): Promise<boolean> {
  if (!wireHash) return false;
  let hash: string;
  try {
    hash = decodePassword(wireHash);
  } catch {
    // A value that predates the codec, or a truncated one. Either way it is
    // not a hash this can check, and a login screen must not throw.
    return false;
  }
  // Spring writes the $2a$ prefix; bcryptjs reads $2a$/$2b$/$2y$ alike.
  return bcrypt.compare(plain, hash);
}
